import { useOrgId } from '../auth';
import { useQuery, useQueryClient } from 'react-query';
import { Uploader } from './uploader';
import { ChangeFinder } from './changeFinder';
import { Downloader } from './downloader';
import { ChunkedDownloader } from './chunkedDownloader';
import { LocalData } from './localData';
import { useEffect, useState } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { Locks } from '../utils/locks';
import { getDownloadProgress, resetDownloadProgress, subscribeToDownloadProgress } from './downloadProgress';
import { ServerError } from '../errors/ServerError';

declare const window: { timeOut: any };

const commonDefinitions = [
    { apiKey: 'equipment', localDataKey: 'Equipment', isGlobal: false },
    { apiKey: 'locations', localDataKey: 'Locations', isGlobal: true },
    { apiKey: 'performedServices', localDataKey: 'PerformedServices', isGlobal: false },
    { apiKey: 'serviceTypes', localDataKey: 'ServiceTypes', isGlobal: true },
    { apiKey: 'equipmentTypes', localDataKey: 'EquipmentTypes', isGlobal: true },
    { apiKey: 'models', localDataKey: 'Models', isGlobal: true },
    { apiKey: 'customers', localDataKey: 'Customers', isGlobal: true },
    { apiKey: 'equipmentFields', localDataKey: 'EquipmentFields', isGlobal: true },
    { apiKey: 'locationFields', localDataKey: 'LocationFields', isGlobal: true },
    { apiKey: 'modelFields', localDataKey: 'ModelFields', isGlobal: true },
    { apiKey: 'questions', localDataKey: 'Questions', isGlobal: true },
    { apiKey: 'serviceMonthCategories', localDataKey: 'ServiceMonthCategories', isGlobal: true },
    { apiKey: 'users', localDataKey: 'Users', isGlobal: true },
    { apiKey: 'history', localDataKey: 'History', isGlobal: false },
    { apiKey: 'locationOverrides', localDataKey: 'LocationOverrides', isGlobal: false },
    { apiKey: 'serviceMonths', localDataKey: 'ServiceMonths', isGlobal: false },
    { apiKey: 'deficiencies', localDataKey: 'Deficiencies', isGlobal: false },
    { apiKey: 'floorPlans', localDataKey: 'FloorPlans', isGlobal: false },
    { apiKey: 'schedule', localDataKey: 'ScheduledEvents', isGlobal: true },
    { apiKey: 'organization', localDataKey: 'organization', isGlobal: true },
];

const uploader = new Uploader();
const downloader = new ChunkedDownloader();
const legacyDownloader = new Downloader();

const uploadDefinitions = [ ...commonDefinitions ];

const downloadDefinitions = [
    { apiKey: 'globalAlerts', localDataKey: 'GlobalAlerts', isGlobal: true },
    ...commonDefinitions
];

for (const definition of uploadDefinitions) {
    uploader.addDefinition({
        apiKey: definition.apiKey, 
        changeFinder: new ChangeFinder(definition.localDataKey)
    });
}

for (const definition of downloadDefinitions) {
    const localData = new LocalData(definition.localDataKey);
    downloader.addDefinition({
        apiKey: definition.apiKey, 
        localData,
        isGlobal: definition.isGlobal
    });
    legacyDownloader.addDefinition({
        apiKey: definition.apiKey,
        localData,
        isGlobal: definition.isGlobal
    });
}

function setEndpoints(orgId: string) {
    downloader.setEndpoint(`/organizations/${orgId}/sync-v2`)
    legacyDownloader.setEndpoint(`/organizations/${orgId}/sync`)
    uploader.setEndpoint(`/organizations/${orgId}/sync`)
}

export function useSync() {
    const queryClient = useQueryClient();
    const orgId = useOrgId();
    const [isUploading, setIsUploading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(getDownloadProgress());
    const networkStatus = useNetworkStatus();

    useEffect(() => {
        return subscribeToDownloadProgress(setDownloadProgress);
    }, []);

    useEffect(() => {
        if (orgId) {
            setEndpoints(orgId);
        }
    }, [orgId]);
 
    async function upload() {
        if (orgId) {
            setEndpoints(orgId);
        }
        setIsUploading(true)    
        clearTimeout(window.timeOut)
        window.timeOut = setTimeout(doUpload, 300);
    }

    async function doUpload() {    
        await Locks.getLockBeforeRunning('upload', async () => {
            try {
                await uploader.upload();
            } catch(e) {
                console.log('Failed to upload', e)
            }
            setIsUploading(false)

            queryClient.refetchQueries({ queryKey: 'getSyncInfo' })
            queryClient.refetchQueries({ queryKey: 'numUnsyncedChanges' })
        })
    }

    async function download() {
        try {
            await downloader.download();
        } catch(error) {
            if (error instanceof ServerError && error.status === 404) {
                await legacyDownloader.download();
                resetDownloadProgress();
                return;
            }

            throw error;
        }
    }

    const { refetch, isLoading, isFetching } = useQuery(
        ['download', orgId], 
        async () => {
            if (!orgId) {
                return;
            }
            setEndpoints(orgId);
            await Locks.getLockBeforeRunning('download', async () => {
                await download();
                queryClient.refetchQueries({ queryKey: 'useData' })
                queryClient.refetchQueries({ queryKey: 'getSyncInfo' })
            })
        },
        {
            enabled: Boolean(orgId),
            refetchOnMount: 'always',
            refetchOnReconnect: true,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    const { data: syncInfo } = useQuery(
        ['getSyncInfo'], 
        async () => {
            const lastSuccessfulDownloadDate = await downloader.getLastSuccessfulDownloadDate();
            const lastSuccessfulUploadDate = await uploader.getLastSuccessfulUploadDate();
            const lastTrySuccessStatus = await uploader.getLastTrySuccessStatus();
            return {
                lastSuccessfulDownloadDate, 
                lastSuccessfulUploadDate,
                lastTrySuccessStatus
            }
        }
    );

    const isDownloading = isLoading || isFetching || downloadProgress.status === 'starting' || downloadProgress.status === 'downloading';

    return {
        upload,
        refetch,
        isDownloading,
        isUploading,
        downloadProgress,
        lastSuccessfulDownloadDate: syncInfo?.lastSuccessfulDownloadDate,
        lastSuccessfulUploadDate: syncInfo?.lastSuccessfulUploadDate,
        lastTrySuccessStatus: syncInfo?.lastTrySuccessStatus || 'yes'
    }
}
